import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';
import { QUERY_KEY } from '@/modules/deductibles/SpendTracker/queries/useDeductibles.query.ts';

import { EnrollmentFormFields } from '../hooks/useEnrollmentForm';

import { useEmployeeEnrollmentParams } from './useEmployeeEnrollmentParams';

export interface NewHealthPlanEnrollmentPayload {
  employee_id: number;
  health_plan_id: number;
  enrollment_status: string;
  effective_date: string;
  coverage_tier: string;
}

export const useCreateHealthPlanEnrollmentQuery = () => {
  const queryClient = useQueryClient();
  const { employeeId } = useEmployeeEnrollmentParams();

  const { isLoading, mutateAsync } = useMutation(
    (newEnrollment: NewHealthPlanEnrollmentPayload) =>
      api.post(API_URL.HEALTH_PLANS_ENROLLMENTS, newEnrollment),
    {
      onSuccess: async () => {
        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: [QUERY_KEY],
          });
        }, 2000);
      },
    }
  );

  const preparedNewEnrollment = useCallback(
    (enrollmentData: EnrollmentFormFields) => {
      return {
        employee_id: Number(employeeId),
        health_plan_id: Number(enrollmentData.healthPlan ?? ''),
        enrollment_status: enrollmentData.healthPlanEnrollmentStatus ?? '',
        effective_date: enrollmentData.healthPlanEnrollmentDate
          ? dayjs(enrollmentData.healthPlanEnrollmentDate).format(RAW_DATE_FORMAT)
          : '',
        coverage_tier: enrollmentData.healthPlanCoverageLevel ?? '',
      };
    },
    [employeeId]
  );

  const handleSave = async (employeeData: EnrollmentFormFields) => {
    const preparedDataPayload = preparedNewEnrollment(employeeData);
    await mutateAsync(preparedDataPayload);
  };

  return {
    onSave: handleSave,
    isLoading,
  };
};
